
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogClose } from '@angular/material/dialog';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { Functions } from '@angular/fire/functions';
import { ListingPublic } from '../../../../globalTypes/listings.types';
import { ParcelUnitsComponent } from "./parcel-units.component";
import { ParcelDimensionsComponent } from "./parcel-dimensions.component";
import { uiErrorCatcher } from '../../helper/error.handling';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AcceptShippingRateData, AcceptShippingRateVariables, GetShippingRateInfoData } from '../../../../globalTypes/thirdPartyTypes';
import { MatIcon } from "@angular/material/icon";
import { CurrencyPipe, JsonPipe } from '@angular/common';
import { ButtonComponent } from "../layout/element/button.component";
import { RateSelectorComponent } from "./rate-selector.component";
import { OrderItem } from '../../../../globalTypes/order.types';
import { getFulfillment, GetFulfillmentVariables, UpdateShipmentIsBeingReconfiguredVariables, upsertParcelDimensions, UpsertParcelDimensionsVariables } from '@dataconnect/account';
import { LoadingLogoComponent } from "../layout/element/loading-logo.component";
import { Parcel, ShippingDistanceUnit, ShippingWeightUnit } from '../../../../globalTypes/fulfillment.types';
import { SteppersComponent } from '../layout/element/steppers.component';
import { StepperData } from '../layout/element/stepper.component';
import { v4 as uuidV4 } from 'uuid';
import { filter, first, lastValueFrom, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { accountFeature } from '../../store/account.store';
import { ordersFeature } from '../../store/order.store';
import { addShippingRateToOrderItemVariables } from '../../../../globalTypes/onCall.types';
import { onCallFunctions } from '../../../../globalTypes/backend.api.types';
import { Allotments, getAllotmentVarsForDoubleCheck, normalizeShippingDimensions } from '../../../../globalTypes/_helpers';

export type AcceptShippingRateDialogMode = "parcel" | "rate" | "final" | "print"

export interface AcceptShippingRateDialogData {
  orderId: string
  fulfillmentId: string
  locationId: string
  rateId: string
  rateCost?: number
  trackingNumber?: string
  trackingUrl?: string
  labelUrl?: string
  messageToBuyer?: string
  startingMode?: AcceptShippingRateDialogMode
  shipmentIsBeingReconfigured?: boolean
  orderShippingAllocations: Allotments
  editedDimensions: {
    id?: string
    name?: string
    length: number
    width: number
    height: number
    weight: number
    distanceUnit: ShippingDistanceUnit
    weightUnit: ShippingWeightUnit
  }[]
}

interface OriginalParcelDimensions {
  id?: string
  length: number
  width: number
  height: number
  weight: number
  distanceUnit: string
  weightUnit: string
}

@Component({
  selector: 'msh-accept-shipping-rate',
  imports: [FormsModule, SteppersComponent, MatDialogClose, CurrencyPipe, ParcelUnitsComponent, ParcelDimensionsComponent, MatIcon, RateSelectorComponent, LoadingLogoComponent, ButtonComponent],
  template: `
   <div class="text-copy m-8 mb-0 overflow-auto max-h-[80vh] flex flex-col gap-1 items-center">

    <msh-steppers [steps]="steps" (stepClicked)="onStepClicked($event)"></msh-steppers>
    @if(loading){
      <div class=" p-8">
        <div class="text-center mb-64">Loading Current Shipment Details</div>
        <msh-loading-logo class="w-96 h-96 mx-auto"></msh-loading-logo>
      </div>
    } @else {
      <div class="flex gap-4 items-center mb-3">
        <div class="text-sm">
          <div class="mt-4 font-bold">From:</div>
          <div>{{addressFrom.attn}}</div>
          <div>{{addressFrom.streetNo}} {{addressFrom.street1}}</div>
          <div>{{addressFrom.city}}, {{addressFrom.state}} {{addressFrom.zip}}</div>
        </div>
        <div class="mt-4 text-sm">
          <div class=" font-bold">To:</div>
          <div>{{addressTo.attn}}</div>
          <div>{{addressTo.streetNo}} {{addressTo.street1}}</div>
          <div>{{addressTo.city}}, {{addressTo.state}} {{addressTo.zip}}</div>
        </div>
        <div class=" mt-2 flex gap-2 justify-between cursor-pointer">
          <img class=" h-10" [src]="rate.providerImage200" />
          <div>
            <div class="flex gap-2">
              <div>{{rate.provider}}</div>
              <div>{{rate.amount | currency}}</div>
              <div>{{rate.servicelevel.name}}</div>
            </div>
            <div class=" text-xs max-w-80">{{rate.durationTerms }}</div>
          </div>
        </div>
      </div>
      <div class="flex gap-4 justify-between">
        @if(editMode === "parcel"){
          <div class=" mt-8">
            @for(parcel of parcels; track parcel; let index = $index){
              <div (click)="viewParcel(index)" [class]="' my-2 p-2 pb-3 pl-3 border cursor-pointer rounded  '+ (parcelErrorExist[index] ? 'text-red-500 ' : '') + (viewingParcelIndex !== index ? 'border-copy ' : 'border-orange-500 ')">
                <div class=" font-bold">{{parcel.name ? parcel.name : 'Parcel '+index + 1}}</div>
                <div class=" text-xs">Dimensions: {{parcel.length}} x {{parcel.width}} x {{parcel.height}} {{parcel.distanceUnit}} - {{parcel.weight}} {{parcel.weightUnit}}</div>
              </div>
            }
          </div>
          <div>
            <div class="flex justify-between">
              <div><!-- @if(parcels.length > 1){ <mat-icon class="opacity-50 hover:text-red-500 hover:opacity-100 cursor-pointer" (click)="deleteParcel(viewingParcelIndex)">delete</mat-icon>} --></div>
              <msh-parcel-units [readonly]="!dimensionsCanStillBeEdited" class=" flex justify-end mt-2 mr-6 text-xs " (parcelUpdated)="onParcelChange($event)" [(parcel)]="parcels[viewingParcelIndex]"></msh-parcel-units>
            </div>
            <msh-parcel-dimensions [readonly]="!dimensionsCanStillBeEdited" class=" text-sm" (parcelUpdated)="onParcelChange($event)" [(parcel)]="parcels[viewingParcelIndex]"></msh-parcel-dimensions>
          </div>
        } @else if(editMode === "rate"){
          <msh-rate-selector 
            [title]="'Select Shipping Rate'"
            [autoLoad]="autoLoadShippingRates"
            [orderItems]="orderItems"
            [shipmentLotId]="shipmentLotId"
            [parcels]="parcels"
            (rateUpdated)="rateUpdated($event)"
          ></msh-rate-selector>
        } @else if(editMode === "final"){
          <div class="flex justify-center items-center gap-8 mt-2">
            @if(this.trackingNumber){

            } @else {
              <div class="">
                <div class=" mt-2 flex gap-2 justify-center flex-wrap">
                  @for(parcel of parcels; track parcel; let index = $index){
                    <div [class]="' my-2 p-2 pb-3 pl-3 border cursor-pointer rounded  border-copy '">
                      <div class=" font-bold">{{parcel.name ? parcel.name : 'Parcel '+index + 1}}</div>
                      <div class=" text-xs">Dimensions: {{parcel.length}} x {{parcel.width}} x {{parcel.height}} {{parcel.distanceUnit}} - {{parcel.weight}} {{parcel.weightUnit}}</div>
                    </div>
                  }
                </div>
                <div class=" flex gap-2 items-center my-4">
                  <mat-icon class="text-yellow-500">warning</mat-icon> 
                  <div class=" text-xs  max-w-48">You are responsible for any overage charges if the the dimensions of your parcel are incorrect.</div>
                </div>
              </div>
              <div class="mb-4">
                @if(doubleCheckAllotmentVars; as v){
                  <div class="mb-4 flex flex-col gap-4 items-center text-sm text-center max-w-60">
                    <div class=" text-lg font-bold">Shipping Allotment</div>
                    {{v.remainingFundStatement}}{{v.remainingFunds | currency:'USD'}}{{v.conjunction}}{{v.conjunction ? v.fulfillmentsLeft : ''}}{{v.fulfillmentsLeftStatement}}
                    @if(v.promisedFunds !== undefined && v.promisedFunds > 0){
                      (expected to total {{v.promisedFunds | currency:'USD'}})
                    }
                    @if(v.showWarning){
                      <div class=" text-red-500 flex gap-2 items-center">
                        Any extra funds needed for shipping will be deducted from your payout.
                      </div>
                    }
                  </div>
                }
                <msh-button [overrideClass]="' !bg-green-500 '" [isLoading]="isFinalizing" (click)="finalizeShippingRate()" cta="Finalize Rate" ></msh-button>

              </div>            
            }
          </div>
        } @else if(editMode === "print"){
          <div class="flex flex-col justify-center items-center gap-4 mt-16 mb-16">
            <a [href]="labelUrl" target="_blank" class=" border rounded p-4 border-green text-green-500 font-bold">Download Shipping Label</a>
            <!-- TODO add tracking info and link to tracking url -->
            @if(trackingNumber){
              <div class=" text-sm" target="_blank">Tracking #<a href="{{trackingUrl}}" class=" text-orange-400">{{trackingNumber}}</a></div>
            }
            <div (click)="dialogRef.close({action: 'shipment_started'})" class="mt-4 lil-action">Mark as Shipped</div>
          </div>
        }
      </div>

    }
   </div>
  <div class=" text-copy bg-copy/10 flex justify-between py-4 px-6 gap-3">
      <button mat-flat-button [mat-dialog-close]="false">Close</button>
      @if(editMode === 'parcel' && !trackingNumber){
        <button [disabled]="loading || !changeMade" (click)="save()">Save</button>
      }
  </div>
  `,
  styles: ``
})
export class AcceptShippingRateDialogComponent implements OnInit {
  loading = true;
  changeMade = false;
  autoLoadShippingRates = false;
  doubleCheckAllotmentVars:any = {};
  parcels: Partial<ListingPublic & {id: string, shippingProcessorParcelId: string}>[] = []
  originalParcels: {distanceUnit: string, height: number, length: number, width: number, weight: number, massUnit: string, shippingProcessorParcelId: string}[] = []
  addressFrom: any
  addressTo: any
  rate: any
  editMode: AcceptShippingRateDialogMode = "parcel";
  orderItems: OrderItem[] = [];
  locationId: string = "";
  orderId: string = "";
  viewingParcelIndex = 0;
  parcelsDeleted: string[] = [];
  dimensionsCanStillBeEdited = true;
  parcelErrorExist: boolean[] = [];
  parcelDifferencesExist: (Partial<ListingPublic & {id: string, shippingProcessorParcelId: string}> | false)[] = [];
  shipmentLotId: undefined | string = undefined;
  rateId: undefined | string = undefined;
  shippingRateInfo?: GetShippingRateInfoData;
  isFinalizing = false;
  trackingNumber = '';
  trackingUrl = '';
  labelUrl = '';
  steps: StepperData[] = [
    {step: 'parcel', title: "Parcel", completed: false, isSelected: true, state: "available"},
    {step: 'rate', title: "Shipping Rate", completed: false, state: "available"},
    {step: 'final', title: "Finalize", completed: false, isDisabled: true, state: "available"},
    {step: 'print', title: "Print", completed: false, isDisabled: true, state: "checked"},
  ]
  

  
  constructor(
    private functions: Functions,
    private snackBar: MatSnackBar,
    private store: Store,
    public dialogRef: MatDialogRef<AcceptShippingRateDialogComponent>, 
    @Inject(MAT_DIALOG_DATA) public data: AcceptShippingRateDialogData
  ){}

  async ngOnInit(): Promise<void> {
    // get rate info 
    if(this.data.startingMode){
      this.setEditMode(this.data.startingMode);
    }
    this.locationId = this.data.locationId;
    if(!this.locationId){
      const locationId = (await lastValueFrom(this.store.select(accountFeature.selectLocation).pipe(filter(l=>!!l), take(1))) );
      this.locationId = locationId as unknown as string;
    }
    if(this.data.trackingNumber){
      this.trackingNumber = this.data.trackingNumber;
    }
    if(this.data.trackingUrl){
      this.trackingUrl = this.data.trackingUrl;
    }
    if(this.data.labelUrl){
      this.labelUrl = this.data.labelUrl;
    }
    this.orderId = this.data.orderId;
    this.shipmentLotId = this.data.fulfillmentId;
    if(!this.orderId){
      const orderId = (await lastValueFrom(this.store.select(ordersFeature.selectOrder).pipe(filter(o=>!!o), take(1))) );
      this.orderId = orderId as unknown as string;
    }
    if(this.data.rateId){
      this.rateId = this.data.rateId;
      this.shippingRateInfo = await this.getShippingRateInfo();
      console.log({shippingRateInfo: this.shippingRateInfo})
      if(this.shippingRateInfo){
        this.setShippingRate(this.shippingRateInfo);
      }
    }
    this.parcels = this.data.editedDimensions.map((editedParcel, idx) => {
      const {id, distanceUnit, height, length, width, weight, weightUnit} = editedParcel;
      return {
        id,
      distanceUnit: distanceUnit,
      weightUnit,
      length,
      height,
      width,
      weight,
    } as Parcel})
    this.steps = this.steps.map(s => {
      if(s.step === "parcel") return {...s, title: "Parcel" + (this.parcels.length > 1 ? "s" : "")}
      return s;
    })
    this.onParcelChange(null);
  }

  async getShippingRateInfo(){
    if(!this.rateId) return;
    const callable = httpsCallable<{rateId: string},GetShippingRateInfoData>(this.functions, onCallFunctions.getShippingRateInfo);
    console.log({existingShippingRateID: this.rateId, starter: this.data.rateId})
    const results: HttpsCallableResult<GetShippingRateInfoData> = await uiErrorCatcher(callable({rateId: this.rateId}),this.snackBar,"Error getting shipping details")
    console.log({results, existingShippingRateID: results.data?.rate?.objectId, existingShipmentId: results.data?.shipment?.objectId})
    this.loading = false;
    return results.data;
  }

  setShippingRate(info: {rate:GetShippingRateInfoData["rate"], shipment?:GetShippingRateInfoData["shipment"]}){
    if(info.shipment?.parcels){
      this.originalParcels = info.shipment.parcels.map((parcel: any) => {
        const {id, distanceUnit, height, length, width, weight, massUnit, objectId} = parcel
        return {
          id,
          distanceUnit,
          height: parseFloat(height),
          length: parseFloat(length),
          width: parseFloat(width),
          weight: parseFloat(weight),
          massUnit: massUnit,
          shippingProcessorParcelId: objectId,
        }
      }); 
    }
    if(info.shipment?.addressFrom && info.shipment?.addressTo){
      const { addressFrom, addressTo} = info.shipment
      // FIXME: Handle location changing its address after order was placed
      this.addressFrom = addressFrom;
      this.addressTo = addressTo;
    }
    this.rate = info.rate;
  }

  async rateUpdated(rateId: string){
    this.rateId = rateId;
    console.log({rateId})
    const rateInfo: GetShippingRateInfoData | undefined = await this.getShippingRateInfo();
    if(rateInfo){
      await this.updateParcelDimensions(rateInfo.shipment?.parcels || []);
      if(this.rateId && this.rateId !== this.data.rateId){
      const vars: addShippingRateToOrderItemVariables = {
        orderId: this.orderId,
        orderItemIds: this.orderItems.map(i=>i.id),
        shippoRateId: this.rateId,
        shipmentLotId: this.shipmentLotId
      }
      console.log({vars})
      const callable = httpsCallable(this.functions,  onCallFunctions.addShippingRateToOrderItem);
      const results: HttpsCallableResult = await callable(vars)
      console.log({results})
    }
      this.setShippingRate(rateInfo);
      this.onParcelChange(null)
      this.changeMade = false;
    }
  }

  async updateParcelDimensions(parcels: GetShippingRateInfoData["shipment"]["parcels"]){
    for(let i = 0; i < parcels.length; i++){
      const parcel = parcels[i];
      const {height, width, length, weight, distanceUnit, massUnit, objectId} = parcel;
      const currentParcel = this.parcels[i];
      if(!parcel) continue; // 
      const id = currentParcel.id || uuidV4();
      const fulfillmentId = this.data.fulfillmentId;
      const parcelInfoToSave: UpsertParcelDimensionsVariables = {
        parcelId: id,
        name: currentParcel.name || '',
        height: parseFloat(height),
        length: parseFloat(length),
        width: parseFloat(width),
        weight: parseFloat(weight),
        distanceUnit: distanceUnit,
        weightUnit: massUnit,
        fulfillmentId,
        shippingProcessorParcelId: parcel.objectId || currentParcel.shippingProcessorParcelId ||undefined,
        shipmentIsBeingReconfigured: true, // This will be updated immediately once the shipping rate is accepted
      }
      try {
        console.log({parcelInfoToSave})
        const results = await upsertParcelDimensions(parcelInfoToSave);
        // const fulfillmentBeingReconfigured = this.fulfillments.find(f => f.id === fulfillmentId);
        //if(fulfillmentBeingReconfigured) fulfillmentBeingReconfigured.shipmentIsBeingReconfigured = true;
        // console.log(this.fulfillments)
        if(!results) this.snackBar.open("Error saving parcel dimensions. Please try again.", "Close", {duration: 3000});
      } catch(errors){
        console.error("Error saving parcel dimensions", errors);
        this.snackBar.open("Error saving parcel dimensions. Please try again.", "Close", {duration: 3000});
        return;
      }
    }  
  }
  
  shippingCostChange(ev: number){
    this.data.rateCost = ev
  }

  checkForDifferencesInParcels(){
    this.parcelDifferencesExist = [];
    this.parcelErrorExist = [];
    this.parcels.forEach((parcel, idx) => {
      const updatedParcel: Partial<ListingPublic & { id: string, shippingProcessorParcelId: string}> = normalizeShippingDimensions<Partial<ListingPublic & {id: string, shippingProcessorParcelId: string}>>({...parcel}, 'length', 'width', 'height')
      const original = this.originalParcels[idx];
      console.log({
        original,
        current: parcel,
        lengthDiff: !original || updatedParcel.length !== original.length,
        widthDiff: !original || updatedParcel.width !== original.width,
        heightDiff: !original || updatedParcel.height !== original.height,
        weightDiff: !original || updatedParcel.weight !== original.weight,
        distanceUnitDiff: !original || updatedParcel.distanceUnit !== original.distanceUnit,
        weightUnitDiff: !original || updatedParcel.weightUnit !== original.massUnit,
        // shippingProcessorParcelIdDiff: !original || updatedParcel.shippingProcessorParcelId !== original.shippingProcessorParcelId,
      })
      this.parcelDifferencesExist[idx] = (!original || updatedParcel.length !== original.length || updatedParcel.width !== original.width || updatedParcel.height !== original.height || updatedParcel.weight !== original.weight || updatedParcel.distanceUnit !== original.distanceUnit || updatedParcel.weightUnit !== original.massUnit) ? updatedParcel : false;
      this.parcelErrorExist[idx] = !updatedParcel.length || !updatedParcel.width || !updatedParcel.height || !updatedParcel.weight
    })
  }

  onParcelChange(ev: any){
    this.checkForDifferencesInParcels();
    let firstStep = this.steps[0];
    console.log({data: this.data  , trackingNumber: this.data.trackingNumber})
    const parcelErrorExist = Object.values(this.parcelErrorExist).some(exist => exist);
    const parcelDifferencesExist = Object.values(this.parcelDifferencesExist).some(difference => difference);
    if(parcelErrorExist){
      // parcel details error, cannot move forward until resolved
      this.viewingParcelIndex = this.parcelErrorExist.findIndex(exist => exist);
      this.steps = this.steps.map(s => {
        if(s.step === "parcel"){
          return {...s, state: "warn"}
        } else if(s.step === "rate" || s.step === "final" || s.step === "print"){
          return {...s, state: "disabled"}
        } else {
          return s;
        }
      })
      return;
    }
    if(parcelDifferencesExist){
      // rate
      this.steps = this.steps.map(s => {
        if(s.step === "parcel"){
          return {...s, state: "available"}
        } else if(s.step === "rate"){
          firstStep = s;
          return {...s, state: "warn"}
        } else if(s.step === "final" || s.step === "print"){
          return {...s, state: "disabled"}
        } else {
          return s;
        }
      })
    } else if(this.trackingNumber === ""){
      // finalize
      console.log({orderShippingAllocations: this.data.orderShippingAllocations})
      this.doubleCheckAllotmentVars = getAllotmentVarsForDoubleCheck(this.data.orderShippingAllocations)
      console.log({shipmentIsBeingReconfigured: this.data.shipmentIsBeingReconfigured})
      if(this.data.shipmentIsBeingReconfigured){
        console.log("shipmentIsBeingReconfigured should be updated")
        const vars: UpdateShipmentIsBeingReconfiguredVariables = {
          locationId: this.data.locationId,
          fulfillmentId: this.data.fulfillmentId,
          shipmentIsBeingReconfigured: false,
        }
        const callable = httpsCallable(this.functions, onCallFunctions.updateShipmentIsBeingReconfigured);
        callable(vars);
      }
      this.steps = this.steps.map(s => {
        if(s.step === "final"){
          if(s.step === "final") firstStep = s;
          return {...s, state: "available", isDisabled: false}
        } else if(s.step === "parcel" || s.step === "rate"){
          return {...s, state: "checked"}
        } else if(s.step === "print"){
          return {...s, state: "disabled"}
        } else {
          return s;
        }
      })
    } else if(!this.trackingNumber){
      // print
      this.steps = this.steps.map(s => {
        if(s.step === "rate"){
          firstStep = s;
          return {...s, state: "available", isDisabled: false}
        } else if(s.step === "final" || s.step === "print"){
          return {...s, state: "disabled"}
        } else {
          return s;
        }
      })
    } else {
      this.steps = this.steps.map(s => {
        if(s.step === "print"){
          firstStep = s;
          return {...s, state: "checked", isDisabled: false}
        } else {
          return {...s, state: "checked", isDisabled: true}
        }
      })
    }
    if(ev === null && firstStep) {
      this.onStepClicked(firstStep);
      this.changeMade = false;
    } else if(this.parcelDifferencesExist.some(difference => !!difference)) {
      this.changeMade = true;
    }
  }

  onStepClicked(step: StepperData){
    console.log({step, editMode: this.editMode})
    if(step.step === "parcel"){
      this.setEditMode("parcel")  ;
    } else if(step.step === "rate"){
      this.setEditMode("rate");
      this.autoLoadShippingRates = this.parcelDifferencesExist.some(difference => !!difference)
      this.changeRates();
    } else if(step.step === "final"){
      this.setEditMode("final");
      // do nothing, just let them click the accept button
    } else if(step.step === "print"){
      this.setEditMode("print");
      
    }
  }

  setEditMode(mode: AcceptShippingRateDialogMode){
    this.editMode = mode;
    this.steps = this.steps.map(s => ({...s, isSelected: s.step === mode}))
  }

  viewParcel(index: number){
    this.viewingParcelIndex = index;
  }
  deleteParcel(index: number){
    const deletedParcel = this.parcels.splice(index, 1)[0];
    if(deletedParcel.id) this.parcelsDeleted.push(deletedParcel.id);
    this.originalParcels.splice(index, 1);
    this.parcelDifferencesExist.splice(index, 1);
    if(this.viewingParcelIndex >= this.parcels.length){
      this.viewingParcelIndex = this.parcels.length - 1;
    }
    this.steps = this.steps.map(s => {
      if(s.step === "parcel"){
        return {...s, title: "Parcel" + (this.parcels.length > 1 ? "s" : "")}
      } else {
        return s;
      }
    })
    this.onParcelChange(null);
  }

  async changeRates(){
    console.log({orderItemsBefore: this.orderItems, orderId: this.orderId, locationId: this.locationId})
    if(this.orderItems.length === 0) {
      const vars: GetFulfillmentVariables = {
        fulfillmentId: this.data.fulfillmentId,
        locationId: this.data.locationId
      }
      const fulfillmentRef = await getFulfillment(vars);
      const fulfillment = fulfillmentRef.data.fulfillments.filter(f => f.id === this.data.fulfillmentId)[0];
      this.orderItems = fulfillment.parcels_on_fulfillment.flatMap(parcel => parcel.orderItems_on_parcel) as unknown as OrderItem[];
    }
  }

  async finalizeShippingRate(){
    if(!this.rateId || !this.data.fulfillmentId) return;
    this.isFinalizing = true;
    const callable = httpsCallable<AcceptShippingRateVariables,AcceptShippingRateData>(this.functions, onCallFunctions.acceptShippingRate);
    try {
      const result: HttpsCallableResult<AcceptShippingRateData> = await callable({rateId: this.rateId, fulfillmentId: this.data.fulfillmentId});
      console.log({result});
      const {shippingTransactionId, trackingNumber, labelUrl, trackingUrl} = result.data;
      // TODO update parent with tracking information
      // .shippingTransactionId = shippingTransactionId;
      // fulfillment.rateCost = shippingCost || 0;
      this.trackingNumber = trackingNumber || "";
      this.trackingUrl = trackingUrl || "";
      this.labelUrl = labelUrl || "";
      this.isFinalizing = false;
      this.onStepClicked(this.steps.find(s => s.step === "print")!)
      this.onParcelChange(null);
      // if(fulfillment.shippingRateId && fulfillment.shippingTransactionId){
      //   this.openFulfillmentPanelToGetLabelOrEnterManualInfo(fulfillment)
      // }
    } catch(error){
      console.error("Error finalizing shipping rate", error);
      this.snackBar.open("Error finalizing shipping rate. Please try again.", "Close", {duration: 3000});
      this.isFinalizing = false;
      return;
    }
  }

  async save(){
    // check that parcels have all dimensions filled in
    let parcelsWithError = 0
    this.parcels.forEach((parcel, idx) => {
      if(!parcel.weight || !parcel.length || !parcel.width || !parcel.height){
        parcelsWithError++;
      }
    });
    if(parcelsWithError > 0){
      const p = parcelsWithError > 1 ? "parcels have" : "parcel has"
      this.snackBar.open(`Please fill in all dimensions and weight inputs the ${p}`, "Close", {duration: 3000});
      return;
    }
    console.log({parcelDifferencesExist: this.parcelDifferencesExist})
    // save parcels with differences from the original
    if(this.parcelsDeleted.length > 0){
      // TODO delete parcels that were deleted in the UI from the fulfillment in Firestore, and then handle the deletion in the fulfillment order details component to remove them from the local state there as well
    }
    if(this.parcelDifferencesExist.some(difference => !!difference) ){
      for(let i = 0; i < this.parcelDifferencesExist.length; i++){
        const parcel = this.parcelDifferencesExist[i];
        if(!parcel) continue;
        const id = parcel.id || uuidV4();
        const fulfillmentId = this.data.fulfillmentId;
        const parcelInfoToSave: UpsertParcelDimensionsVariables = {
          parcelId: id,
          name: parcel.name || '',
          height: parcel.height!,
          length: parcel.length!,
          width: parcel.width!,
          weight: parcel.weight!,
          distanceUnit: parcel.distanceUnit!,
          weightUnit: parcel.weightUnit!,
          fulfillmentId,
          shipmentIsBeingReconfigured: true, // This will be updated immediately once the shipping rate is accepted
        }
        try {
          console.log({parcelInfoToSave})
          const results = await upsertParcelDimensions(parcelInfoToSave);
          // const fulfillmentBeingReconfigured = this.fulfillments.find(f => f.id === fulfillmentId);
          //if(fulfillmentBeingReconfigured) fulfillmentBeingReconfigured.shipmentIsBeingReconfigured = true;
          // console.log(this.fulfillments)
          if(!results) this.snackBar.open("Error saving parcel dimensions. Please try again.", "Close", {duration: 3000});
        } catch(errors){
          console.error("Error saving parcel dimensions", errors);
          this.snackBar.open("Error saving parcel dimensions. Please try again.", "Close", {duration: 3000});
          return;
        }
      }
    }
  }
}
